import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Spin,
  Col,
  Modal,
  Typography,
  message,
  Alert,
} from 'antd';
import Select from 'components/Select';
import { createDecimalRegExp } from 'utils/patterns';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import { onError } from 'utils/handlers';
import {
  createParticipe,
  deleteParticipe,
  formatParticipe,
  getParticipes,
  updateParticipe,
} from 'api/catastro/participes';
import { getPadrones } from 'utils/padrones';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import { getCiudadanoCaja } from 'api/cuentaunicasir/index';
import { getContribuyente, getContribuyenteCaja } from 'api/empresas/contribuyentes';
import { getCiudadano } from 'api/cuentaunicasir';
import { getPredio, patchPredio, dateKeysPredio } from 'api/catastro/predio';
import { EditOutlined } from '@ant-design/icons';
import formatValues from 'utils/formatValues';
import { capitalize } from 'utils/normalizers';
import { getNombreCompleto } from 'utils/padronesToString';
import removeIndexes from 'views/Tramites/GPM/removeIndexes';
import { getSiglasTiposPropietarios } from 'api/catastro';
import FormSubmitControls from './FormSubmitControls';
import Table from './Table';
import ModalDelete from './ModalDelete';
import { InputSearchPro } from './InputSearch';
import NumericInput from './NumericInput';

const { Text } = Typography;

const avialablePadrones = [
  padronesEnum.CIUDADANO,
  padronesEnum.CONTRIBUYENTE,
];

const gettersPadronesCaja = {
  [padronesEnum.CIUDADANO]: getCiudadanoCaja,
  [padronesEnum.CONTRIBUYENTE]: getContribuyenteCaja,
};

const getterById = {
  [padronesEnum.CIUDADANO]: getCiudadano,
  [padronesEnum.CONTRIBUYENTE]: getContribuyente,
};

export const permissionParticipe = {
  title: 'Participes',
  model: 'participe',
  catalogs: ['ciudadano', 'contribuyente', 'padron'],
};

export const aplicarAfectacionesParticipes = async ({
  participes_a_eliminar: _participes_a_eliminar = [],
  participes_a_agregar: _participes_a_agregar = [],
  participes_a_modificar: _participes_a_modificar = [],
  participe_principal_anterior,
  participes = [],
}) => {
  if (!participes.length) return;
  let participes_a_eliminar = _participes_a_eliminar;
  let participes_a_agregar = _participes_a_agregar;
  let participes_a_modificar = _participes_a_modificar;

  const principalActual = participes.find((p) => p.es_participe_principal);
  const principalAnterior = participe_principal_anterior ? {
    ...participes.find((p) => p.id === participe_principal_anterior),
    id: participe_principal_anterior,
    es_participe_principal: participe_principal_anterior === principalActual.id,
  } : null;

  // se eliminó el principalAnterior ejecutamos deletes para evitar constraint futuros
  if (participes_a_eliminar.includes(principalAnterior?.id)) {
    participes_a_eliminar = participes_a_eliminar.filter((e) => e !== principalAnterior.id);
    await deleteParticipe(principalAnterior.id);
  }
  // se modificó el principalAnterior y dejo de serlo
  // ejecutamos update por separado para evitar constraint
  if (participes_a_modificar.includes(principalAnterior?.id)
    && !principalAnterior?.es_participe_principal) {
    participes_a_modificar = participes_a_modificar.filter((e) => e !== principalAnterior.id);
    await updateParticipe(principalAnterior.id, principalAnterior);
  }
  // se modificó un participe existente que paso a ser el nuevo principal, ejecutamos update
  if (participes_a_modificar.includes(principalActual.id)
    && principalActual.id !== principalAnterior?.id) {
    participes_a_modificar = participes_a_modificar.filter((e) => e !== principalActual.id);
    await updateParticipe(principalActual.id, principalActual);
  }
  // se agregó un participe existente que paso a ser principal, ejecutamos post
  if (participes_a_agregar.includes(principalActual.id)) {
    participes_a_agregar = participes_a_agregar.filter((e) => e !== principalActual.id);
    delete principalActual.id;
    await createParticipe(principalActual);
  }

  await Promise.all(participes_a_eliminar.map((e) => deleteParticipe(e)));
  await Promise.all(participes.filter((p) => participes_a_modificar.includes(p.id))
    .map((p) => updateParticipe(p.id, p)));
  await Promise.all(participes.filter((p) => participes_a_agregar.includes(p.id))
    .map(({ id, ...p }) => createParticipe(p)));
};

const [decimalRegExp] = createDecimalRegExp(null, { post: 4 });

const Participe = ({
  mutable,
  predio = {},
  permission,
  callback,
  fetchCallback,
  noAfectarPadron,
}) => {
  const tiposDePersonas = useSelector((s) => s.catalogs.tiposDePersonas);
  const [padronFound, setPadronFound] = useState([]);
  const [form] = Form.useForm();
  const esParticipePrincipal = Form.useWatch('es_participe_principal', form);
  const tipo_de_persona = Form.useWatch('tipo_de_persona', form);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [padrones, setPadrones] = useState([]);
  const [siglas, setSiglas] = useState([]);
  const selectedPadron = Form.useWatch('padron', form) || 15;
  const [dataExterno, setDataExterno] = useState(null);
  const principal = data.find((e) => e.es_participe_principal)?.id;
  const labelTipoParticipe = esParticipePrincipal ? 'Propietario' : 'Copropietario';

  const getNombresDeParticipes = (l) => l.map((p) => p.descripcion);

  const getInfoParticipePrincipal = (participe) => {
    if (!participe) return null;
    const key = participe.padron === padronesEnum.CIUDADANO ? 'ciudadano' : 'contribuyente';
    const elseKey = participe.padron === padronesEnum.CIUDADANO ? 'contribuyente' : 'ciudadano';
    return {
      participe_principal: participe.descripcion,
      apellido_materno_propietario: participe.apellido_materno,
      apellido_paterno_propietario: participe.apellido_paterno,
      nombre_propietario: participe.nombre_participe,
      nombre_completo: participe.descripcion,
      tipo_de_persona: participe.tipo_de_persona || 1,
      [key]: participe.object_id_participe,
      [elseKey]: null,
    };
  };

  const fetchData = async () => {
    setLoading(true);
    if (predio) {
      if (noAfectarPadron) {
        const _predio = await getPredio(predio.id);
        const json = _predio.json_predio ? _predio.json_predio : removeIndexes(_predio);
        json.participes = json.participes || await getParticipes({ predio: predio.id });
        const infoParticipePrincipal = getInfoParticipePrincipal(json.participes
          .find((p) => p.es_principal));
        if (infoParticipePrincipal) {
          Object.keys(infoParticipePrincipal).forEach((key) => {
            json[key] = infoParticipePrincipal[key];
          });
        }
        json.participe_principal_anterior = json.participe_principal_anterior || json.participes
          .find((e) => e.es_participe_principal)?.id;
        json.participes_a_eliminar = json.participes_a_eliminar || [];
        json.participes_a_agregar = json.participes_a_agregar || [];
        json.participes_a_modificar = json.participes_a_modificar || [];
        json.nombres_de_participes = getNombresDeParticipes(json.participes);
        json.nombres_de_copropietarios = getNombresDeParticipes(json.participes
          .filter((e) => !e.es_participe_principal));
        const json_predio = JSON.stringify(formatValues(json, { clean: false }, dateKeysPredio));
        if (mutable) {
          _predio.json_predio = _predio.json_predio_raw !== json_predio
            ? (await patchPredio(predio.id, { json_predio }, false))?.json_predio
            : json;
        }
        setData(json.participes.map(formatParticipe));
        if (fetchCallback) fetchCallback(json.participes, _predio.json_predio);
        setLoading(false);
        return;
      }
      const _participes = await getParticipes({ predio: predio.id });
      setData(_participes);
      if (fetchCallback) fetchCallback(_participes);
    }
    setLoading(false);
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      const [_padrones, _siglas] = await Promise.all([getPadrones(), getSiglasTiposPropietarios()]);
      setPadrones(_padrones.filter((e) => avialablePadrones.includes(e.id)));
      setSiglas(_siglas);
      await fetchData();
      setLoading(false);
    };
    fetchAll();
  }, [predio.id]);

  useEffect(() => {
    if (esParticipePrincipal && padronFound.length) {
      const [found] = padronFound;
      form.setFieldsValue({
        nombre_participe: found.nombre,
        apellido_paterno: found.apellido_paterno,
        apellido_materno: found.apellido_materno,
      });
    }
  }, [esParticipePrincipal]);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setPadronFound([]);
    setSelectedRowKeys([]);
    setLoading(false);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const { object_id_participe, ...values } = form.getFieldsValue();
      values.padron = form.getFieldValue('padron');
      values.predio = predio.id;
      const [found] = padronFound;

      if (found) {
        values.object_id_participe = object_id_participe;
        // Si el padron es contribuyente, se obtiene el tipo de persona
        // del contribuyente encontrado
        // Si el padron es ciudadano por default se asigna 1 (Fisica)
        values.tipo_de_persona = found.tipo_de_persona || 1;
        if (values.tipo_de_persona === 2) {
          values.descripcion = found.razon_social;
        }
      }
      values.descripcion = values.descripcion || ['apellido_paterno', 'apellido_materno', 'nombre_participe']
        .map((f) => values[f] || '').filter((e) => e).join(' ').trim();
      const patchValues = values.es_participe_principal ? getInfoParticipePrincipal(values) : {};
      const [key] = selectedRowKeys;
      if (noAfectarPadron) {
        const json_predio = { ...predio };
        json_predio.participes = data;
        if (key) {
          const idx = json_predio.participes.findIndex((e) => e.id === selectedRowKeys[0]);
          json_predio.participes[idx] = { ...json_predio.participes[idx], ...values };
        } else {
          values.id = `temp_id_${(new Date()).toISOString()}`;
          json_predio.participes.push(values);
        }
        if (typeof key === 'number' && !json_predio.participes_a_modificar.includes(key)) {
          json_predio.participes_a_modificar.push(key);
        } else if (!key) {
          json_predio.participes_a_agregar.push(values.id);
        }
        json_predio.nombres_de_participes = getNombresDeParticipes(json_predio.participes);
        json_predio.nombres_de_copropietarios = getNombresDeParticipes(json_predio.participes
          .filter((e) => !e.es_participe_principal));
        await patchPredio(predio.id, {
          json_predio: JSON
            .stringify(formatValues({ ...json_predio, ...patchValues }, { clean: false })),
        }, false);
        if (fetchCallback) await callback(`${key ? 'Agregado' : 'Modificado'} correctamente`);
        await fetchData();
        onCancel();
        return;
      }
      const saved = await (key ? updateParticipe(key, values) : createParticipe(values));
      if (saved) {
        if (values.es_participe_principal && !noAfectarPadron) {
          await patchPredio(predio.id, patchValues);
          if (!fetchCallback) await callback(predio.id);
        }
        if (fetchCallback) await callback(`${key ? 'Agregado' : 'Modificado'} correctamente`);
        await fetchData();
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const onClickAdd = () => {
    onCancel();
    const used = data.reduce((acum, curr) => acum + parseFloat(curr.porcentaje_nuevo), 0);
    const porcentaje_nuevo = ((100 * 100 - (used * 100)) / 100).toFixed(4);
    if (!porcentaje_nuevo) {
      message.info('El porcentaje ya ha alcanzado el 100%, actualicé el porcentaje de los demas participes de ser necesario');
      return;
    }
    setVisible(true);
    form.setFieldsValue({
      porcentaje_nuevo,
      es_participe_principal: porcentaje_nuevo === (100).toFixed(4),
    });
  };

  const onClickEdit = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.id === key);
    if (!avialablePadrones.includes(match.padron)) {
      form.setFieldsValue({ padron: 15 });
    }
    if (match?.object_id_participe && avialablePadrones.includes(match.padron)) {
      match.object_id_participe = parseInt(match.object_id_participe, 10);
      const getter = getterById[match.padron];
      const _found = await getter(match.object_id_participe);
      setPadronFound([{ ..._found }]);
    }

    form.setFieldsValue(match);
    setLoading(false);
    setVisible(true);
  };

  const onClickDelete = () => setVisibleAlert(true);

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    let deleted = false;
    if (noAfectarPadron) {
      let json_predio = { ...predio };
      json_predio.participes = json_predio.participes.filter((e) => e.id !== selectedRowKeys[0]);
      if (typeof key === 'number') {
        json_predio.participes_a_eliminar.push(key);
        if (json_predio.participes_a_modificar.includes(key)) {
          json_predio.participes_a_modificar = json_predio.participes_a_modificar
            .filter((e) => e !== key);
        }
      } else {
        json_predio.participes_a_agregar = json_predio.participes_a_agregar
          .filter((e) => e !== key);
      }
      json_predio.nombres_de_participes = getNombresDeParticipes(json_predio.participes);
      json_predio = JSON.stringify(formatValues(json_predio, { clean: false }, dateKeysPredio));
      deleted = await patchPredio(predio.id, { json_predio }, false);
    } else {
      deleted = await deleteParticipe(key);
    }
    if (deleted) {
      if (fetchCallback) await callback('Eliminado correctamente');
      await fetchData();
      onCancel();
      setVisibleAlert(false);
    }
    setLoading(false);
  };

  const rowSelection = mutable ? {
    selectedRowKeys,
    type: 'radio',
  } : null;

  const required = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [required],
    requiredIfPrincipal: [{ required: esParticipePrincipal }],
    porcentaje_nuevo: [
      required,
      {
        validator: async (_, value) => {
          if (value) {
            if (!decimalRegExp.test(value)) {
              throw new Error('Ingrese un entero o decimal válido');
            }
            const [key] = selectedRowKeys;
            const currentPercentaje = (key ? data.filter((e) => e.id !== key) : data)
              .reduce((acum, curr) => acum
                + parseInt(parseFloat(curr.porcentaje_nuevo) * 100, 10), 0);
            const diff = 10000 - currentPercentaje;
            const val = parseInt((parseFloat(value) * 100), 10);
            if (val > diff) throw new Error(`No puede ser mayor a ${diff / 100}`);
          }
        },
      },
    ],
  };

  const columns = [
    {
      titleText: 'Descripcion',
      key: 'descripcion',
      dataIndex: 'descripcion',
      width: 250,
    },
    {
      titleText: 'Porcentaje',
      key: 'porcentaje_nuevo',
      dataIndex: 'porcentaje_nuevo',
      width: 100,
      render: (value) => (
        <span>
          {value}
          %
        </span>
      ),
    },
    {
      titleText: 'Propietario o Copropietario',
      key: 'es_participe_principal',
      dataIndex: 'es_participe_principal',
      render: (es_principal) => (es_principal ? 'Propietario' : 'Copropietario'),
      width: 200,
    },
    {
      titleText: 'Tipo de Propietario',
      key: 'sigla_propietario',
      dataIndex: 'sigla_propietario',
      render: (val) => siglas.find((s) => s.id === val)?.descripcion,
      width: 200,
    },
  ];

  const resetAndValidate = () => {
    setPadronFound([]);
    form.resetFields(['object_id_participe', 'q', 'descripcion']);
    form.validateFields(['q']);
  };

  const onChangeInputSearch = ({ target: { value } }) => {
    if (!value?.length) {
      resetAndValidate();
    }
    setTimeout(() => {
      form.setFieldsValue({ q: value });
    });
  };

  const fetchSearch = async (validate = true, _q = null) => {
    try {
      if (validate) {
        form.validateFields(['q']);
      }
      const q = form.getFieldValue('q') || _q;
      if (q) {
        setLoading(true);
        const getter = gettersPadronesCaja[selectedPadron];
        const _result = await getter({ q });
        const result = _result.results || _result;
        if (!result.length) {
          message.info('No se encontraron resultados');
          setPadronFound([]);
          return;
        }
        if (result.length > 1) {
          setDataExterno(_result);
          document.querySelector('#onclick-advanced-search-q')?.click();
          return;
        }
        const participe = result[0];
        const nombre_completo = getNombreCompleto(participe);
        setPadronFound([{ ...participe, nombre_completo }]);
        form.setFieldsValue({
          object_id_participe: participe.id,
          nombre_participe: participe.nombre,
          apellido_paterno: participe.apellido_paterno,
          apellido_materno: participe.apellido_materno,
          tipo_de_persona: participe.tipo_de_persona || 1,
          descripcion: nombre_completo,
        });
        setTimeout(() => { form.validateFields(['object_id_participe']); });
        setLoading(false);
      }
    } catch (err) {
      onError(err, setLoading);
      setPadronFound([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin tip="Cargando..." spinning={loading} style={{ width: '100%', height: '100%' }}>
      {!fetchCallback && mutable && (
        <Alert
          style={{ marginBlock: 10, borderRadius: 5 }}
          type="warning"
          showIcon
          message={(
            <Typography.Text>
              Importante
            </Typography.Text>
          )}
          description={(
            <>
              Antes de realizar algun cambio en los participes, asegurese de haber guardado los
              cambios del predio para evitar perdida de información. Se recomienda presionar el
              botón
              {' '}
              <EditOutlined />
              {' '}
              <strong>
                Guardar y continuar
              </strong>
              {' '}
              antes de realizar cualquier cambio.
            </>
          )}
        />
      )}
      <Table
        cols={columns}
        data={data}
        rowSelection={rowSelection}
        handleOnRowClick={handleOnRowClick}
        controls={mutable ? { onClickAdd, onClickEdit, onClickDelete } : null}
        allowExport={false}
        allowSort={false}
        allowSearch={false}
        mobileColIndex={0}
        permission={permission}
        hasAllViewPermission
        rowKey="id"
      />
      <Modal
        open={visible}
        closable={false}
        footer={null}
        title={(
          <FormSubmitControls
            label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} ${labelTipoParticipe}`}
            onFinish={onFinish}
            onCancel={onCancel}
            loading={loading}
          />
        )}
        onCancel={onCancel}
        destroyOnClose
      >
        <Spin tip="Cargando..." spinning={loading} style={{ width: '100%', height: '100%' }}>
          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              name="participe"
              onFinish={onFinish}
              initialValues={{ estados_globales: 1, padron: 15, tipo_de_persona: 1 }}
            >
              <Col span={24}>
                <Form.Item
                  name="tipo_de_persona"
                  label="Tipo de persona"
                  rules={rules.required}
                >
                  <Select
                    dataSource={tiposDePersonas}
                    disabled={!!padronFound.length}
                  />
                </Form.Item>
              </Col>
              {tipo_de_persona === 1 ? (
                <>
                  <Col span={24}>
                    <Form.Item
                      name="nombre_participe"
                      label={`Nombre del ${labelTipoParticipe}`}
                      normalize={capitalize}
                      rules={rules.required}
                    >
                      <Input
                        disabled={!!padronFound.length || !!selectedRowKeys.length}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="apellido_paterno"
                      label="Apellido Paterno"
                      rules={rules.required}
                      normalize={capitalize}
                    >
                      <Input
                        disabled={!!padronFound.length || !!selectedRowKeys.length}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="apellido_materno"
                      label="Apellido Materno"
                      normalize={capitalize}
                    >
                      <Input
                        disabled={!!padronFound.length || !!selectedRowKeys.length}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                </>
              ) : (
                <Col span={24}>
                  <Form.Item
                    name="descripcion"
                    label={`Razón social del ${labelTipoParticipe}`}
                    normalize={capitalize}
                    rules={rules.required}
                  >
                    <Input disabled={!!padronFound.length || !!selectedRowKeys.length} allowClear />
                  </Form.Item>
                </Col>
              )}
              <Col span={24}>
                <NumericInput
                  name="porcentaje_nuevo"
                  label="Porcentaje"
                  rules={rules.porcentaje_nuevo}
                  precision={4}
                  decimal
                />
              </Col>
              <Col span={24}>
                <Form.Item
                  name="es_participe_principal"
                  label="Propietario o Copropietario"
                  rules={rules.required}
                >
                  <Select
                    disabled={principal && (selectedRowKeys[0] !== principal)}
                    trueFalse={['Propietario', 'Copropietario']}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="sigla_propietario"
                  label="Tipo de propietario"
                >
                  <Select dataSource={siglas} />
                </Form.Item>
              </Col>
              <InputSearchPro
                rules={rules.requiredIfPrincipal}
                advancedComponent={BusquedaAvanzadaPadrones}
                advancedComponentProps={{
                  tipoDePadron: selectedPadron,
                  allowAdd: true,
                  allowEdit: true,
                  datosContactoOpcionales: true,
                  dataExterno,
                  callback: (values) => {
                    const nombre_completo = getNombreCompleto(values);
                    setPadronFound([{ ...values, nombre_completo }]);
                    form.setFieldsValue({
                      descripcion: nombre_completo,
                      nombre_participe: values.nombre,
                      apellido_paterno: values.apellido_paterno,
                      apellido_materno: values.apellido_materno,
                      object_id_participe: values.id,
                      tipo_de_persona: values.tipo_de_persona || 1,
                    });
                  },
                }}
                label={selectedPadron === 1 ? 'Ciudadano' : 'Contribuyente'}
                resultProps={{
                  dataSource: padronFound,
                  setDataSource: (d) => {
                    const nombre_completo = getNombreCompleto(d);
                    setPadronFound([{ ...d, nombre_completo }]);
                  },
                  name: 'object_id_participe',
                  label: selectedPadron === 1 ? 'Ciudadano' : 'Contribuyente',
                  keyProp: selectedPadron === 1 ? 'clave_ciudadana' : 'clave',
                  render: (item) => {
                    if (!item) return '';
                    return `${item?.clave_ciudadana || item?.clave} - ${getNombreCompleto(item)}`;
                  },
                  onClear: resetAndValidate,
                  allowClear: true,
                  breakPoints: { md: 24 },
                }}
                inputProps={{
                  onPressEnter: () => fetchSearch(),
                  onChange: onChangeInputSearch,
                }}
                prefix={{
                  dataSource: padrones,
                  rules: rules.requiredIfPrincipal,
                  label: 'Padrón',
                  allowClear: false,
                  name: 'padron',
                }}
                breakPoints={{ md: 24 }}
                disabled={!mutable || !!selectedRowKeys.length}
              />
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Col>
        </Spin>
      </Modal>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        loading={loading}
        content={(
          <>
            <Text style={{ fontSize: 16 }}>
              {`¿Desea eliminar el ${labelTipoParticipe} ${form.getFieldValue('descripcion')}?`}
            </Text>
            <Text>
              Solo será posible si no ha sido utilizado en otros procesos
            </Text>
          </>
        )}
      />
    </Spin>
  );
};

Participe.propTypes = {
  mutable: PropTypes.bool,
  predio: PropTypes.shape(),
  permission: PropTypes.shape().isRequired,
  callback: PropTypes.func.isRequired,
  fetchCallback: PropTypes.func,
  noAfectarPadron: PropTypes.bool,
};

Participe.defaultProps = {
  predio: undefined,
  noAfectarPadron: false,
  fetchCallback: null,
  mutable: true,
};

export default Participe;
